import {
  type LucideIcon,
  RefreshCw,
  SearchX,
  ShieldAlert,
  TriangleAlert,
  WifiOff,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils/cn'
import { HumintNotice } from '../../HumintNotice'
import type { VoicePairReadProblem } from './voice-pair-model'
import { PAIR_CARD, PAIR_DECISION_COLUMN, PAIR_GRID, PAIR_MAIN_COLUMN } from './voice-pair-recipes'

const COPY = 'humint.voice.match.problem'
const TATTOO = 'humint.tattooMatch.problem'

export interface VoicePairReadNoticeProps {
  problem: VoicePairReadProblem
  /** Read the comparison again (one more audited read); nothing to try for a pair that is gone. */
  onRetry?: () => void
  /** The read is being made again: *Try again* waits for it. */
  retrying?: boolean
}

/**
 * How each failed read looks and reads. A read refused for its audit record is a warning, and
 * what the tattoo comparison already says of a failed read is said in its words.
 */
const PROBLEM: Record<VoicePairReadProblem, { Icon: LucideIcon; title: string; body: string }> = {
  not_found: { Icon: SearchX, title: `${COPY}.not_found.title`, body: `${COPY}.not_found.body` },
  audit_unavailable: {
    Icon: ShieldAlert,
    title: `${TATTOO}.audit_unavailable.title`,
    body: `${TATTOO}.audit_unavailable.body`,
  },
  offline: { Icon: WifiOff, title: `${TATTOO}.offline.title`, body: `${COPY}.offline.body` },
  error: { Icon: TriangleAlert, title: `${TATTOO}.error.title`, body: `${COPY}.error.body` },
}

/**
 * The comparison when there is no pair to show: the pair is gone, the read could not be recorded
 * in the audit trail (so nothing was returned), no connection, or anything else. Never an empty
 * screen that reads like "no match".
 */
export function VoicePairReadNotice({ problem, onRetry, retrying }: VoicePairReadNoticeProps) {
  const { t } = useTranslation()
  const { Icon, title, body } = PROBLEM[problem]
  return (
    <HumintNotice
      Icon={Icon}
      tone={problem === 'audit_unavailable' ? 'warning' : 'neutral'}
      copy={{ title: t(title), body: t(body) }}
      action={
        onRetry && problem !== 'not_found'
          ? {
              label: t('humint.common.actions.retry'),
              Icon: RefreshCw,
              onClick: onRetry,
              loading: retrying,
            }
          : undefined
      }
    />
  )
}

/**
 * The comparison while its pair is read: its title, the pair's standing, the two sides and the
 * evidence as blocks, where they will stand.
 */
export function VoicePairSkeleton() {
  const { t } = useTranslation()
  return (
    <div role="status" className="@container/voicepair flex min-h-0 min-w-0 flex-1 flex-col">
      <span className="sr-only">{t('humint.voice.match.loading')}</span>
      <div aria-hidden className={PAIR_GRID}>
        <Skeleton className={cn(PAIR_MAIN_COLUMN, 'h-12 w-56')} />
        <Skeleton className={cn(PAIR_DECISION_COLUMN, 'h-20 rounded-2xl @3xl/voicepair:h-56')} />
        <div
          className={cn(
            PAIR_CARD,
            PAIR_MAIN_COLUMN,
            'grid grid-cols-1 gap-3 p-4 @5xl/voicepair:grid-cols-2'
          )}
        >
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-12 rounded-xl @5xl/voicepair:col-span-2" />
        </div>
        <Skeleton className={cn(PAIR_MAIN_COLUMN, 'h-48 rounded-2xl')} />
      </div>
    </div>
  )
}
